import React from 'react'
import {
  IoFlameOutline,
  IoTrophyOutline,
  IoArrowDownOutline,
  IoTrendingUp,
  IoTrendingDown,
  IoShieldOutline,
  IoFootballOutline,
} from 'react-icons/io5'
import type { MatchContextHub } from '../../../types'

interface MatchContextPanelProps {
  context: MatchContextHub
}

export function MatchContextPanel({ context }: MatchContextPanelProps) {
  // Get pressure label
  const getPressureLabel = (pressure: string | null | undefined) => {
    switch (pressure) {
      case 'title_race':
        return 'Title Race'
      case 'top_4':
        return 'Top 4 Battle'
      case 'mid_table':
        return 'Mid-Table'
      case 'relegation':
        return 'Relegation Battle'
      default:
        return null
    }
  }

  // Get pressure icon
  const getPressureIcon = (pressure: string | null | undefined) => {
    switch (pressure) {
      case 'title_race':
        return <IoTrophyOutline size={16} />
      case 'top_4':
        return <IoTrendingUp size={16} />
      case 'relegation':
        return <IoArrowDownOutline size={16} />
      default:
        return <IoShieldOutline size={16} />
    }
  }

  // Get tactical style description
  const getTacticalDescription = (style: string | null | undefined) => {
    switch (style) {
      case 'high_press':
        return 'High-pressing, aggressive style'
      case 'mid_block':
        return 'Balanced mid-block approach'
      case 'low_block':
        return 'Deep, defensive low block'
      default:
        return null
    }
  }

  return (
    <div className="cards-panel match-context-panel">
      {/* Rivalry Section */}
      {context.is_rivalry && (
        <div className="context-section context-rivalry">
          <div className="context-section-header">
            <IoFlameOutline size={20} className="context-icon rivalry" />
            <h4 className="context-section-title">Rivalry Match</h4>
          </div>
          <div className="context-section-content">
            {context.rivalry_name && (
              <p className="context-rivalry-name">{context.rivalry_name}</p>
            )}
            <div className="context-rivalry-stats">
              {context.rivalry_intensity != null && (
                <div className="context-stat">
                  <span className="context-stat-label">Intensity</span>
                  <div className="context-intensity-bar">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <span
                        key={i}
                        className={`context-intensity-dot ${i < context.rivalry_intensity! ? 'filled' : ''}`}
                      />
                    ))}
                  </div>
                </div>
              )}
              {context.rivalry_h2h_cards_avg != null && (
                <div className="context-stat">
                  <span className="context-stat-label">H2H Avg Cards</span>
                  <span className="context-stat-value">{context.rivalry_h2h_cards_avg.toFixed(1)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table Pressure Section */}
      {(context.table_pressure?.home || context.table_pressure?.away) && (
        <div className="context-section context-pressure">
          <div className="context-section-header">
            <IoTrophyOutline size={20} className="context-icon pressure" />
            <h4 className="context-section-title">Table Stakes</h4>
          </div>
          <div className="context-pressure-grid">
            {context.table_pressure?.home && (
              <div className={`context-pressure-card home ${context.table_pressure.home}`}>
                {getPressureIcon(context.table_pressure.home)}
                <span className="context-pressure-team">Home</span>
                <span className="context-pressure-label">
                  {getPressureLabel(context.table_pressure.home)}
                </span>
              </div>
            )}
            {context.table_pressure?.away && (
              <div className={`context-pressure-card away ${context.table_pressure.away}`}>
                {getPressureIcon(context.table_pressure.away)}
                <span className="context-pressure-team">Away</span>
                <span className="context-pressure-label">
                  {getPressureLabel(context.table_pressure.away)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Aggression Section */}
      {context.recent_aggression && (
        <div className="context-section context-aggression">
          <div className="context-section-header">
            <IoTrendingUp size={20} className="context-icon aggression" />
            <h4 className="context-section-title">Recent Aggression (Last 5)</h4>
          </div>
          <div className="context-aggression-grid">
            <div className="context-aggression-card">
              <span className="context-aggression-label">Home Cards/Game</span>
              <span className="context-aggression-value">
                {context.recent_aggression.home_cards_last_5.toFixed(1)}
              </span>
            </div>
            <div className="context-aggression-card">
              <span className="context-aggression-label">Away Cards/Game</span>
              <span className="context-aggression-value">
                {context.recent_aggression.away_cards_last_5.toFixed(1)}
              </span>
            </div>
            {context.recent_aggression.home_fouls_last_5 != null && (
              <div className="context-aggression-card">
                <span className="context-aggression-label">Home Fouls/Game</span>
                <span className="context-aggression-value">
                  {context.recent_aggression.home_fouls_last_5.toFixed(1)}
                </span>
              </div>
            )}
            {context.recent_aggression.away_fouls_last_5 != null && (
              <div className="context-aggression-card">
                <span className="context-aggression-label">Away Fouls/Game</span>
                <span className="context-aggression-value">
                  {context.recent_aggression.away_fouls_last_5.toFixed(1)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tactical Styles Section */}
      {(context.tactical_style_home || context.tactical_style_away) && (
        <div className="context-section context-tactical">
          <div className="context-section-header">
            <IoFootballOutline size={20} className="context-icon tactical" />
            <h4 className="context-section-title">Tactical Styles</h4>
          </div>
          <div className="context-tactical-grid">
            {context.tactical_style_home && (
              <div className="context-tactical-card">
                <span className="context-tactical-team">Home</span>
                <span className="context-tactical-style">
                  {context.tactical_style_home.replace(/_/g, ' ')}
                </span>
                <span className="context-tactical-desc">
                  {getTacticalDescription(context.tactical_style_home)}
                </span>
              </div>
            )}
            {context.tactical_style_away && (
              <div className="context-tactical-card">
                <span className="context-tactical-team">Away</span>
                <span className="context-tactical-style">
                  {context.tactical_style_away.replace(/_/g, ' ')}
                </span>
                <span className="context-tactical-desc">
                  {getTacticalDescription(context.tactical_style_away)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!context.is_rivalry &&
        !context.table_pressure?.home &&
        !context.table_pressure?.away &&
        !context.recent_aggression && (
          <div className="context-empty">
            <IoShieldOutline size={32} />
            <p>No special context factors detected for this match.</p>
          </div>
        )}
    </div>
  )
}
